import React, { useState, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { validateChangePasswordForm } from './validations';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import { Validation } from '../../../interfaces/common';
import { ChangePassword as ChangePasswordInterface } from '../../../interfaces/user.interface';
import { AuthContext } from '../../../contexts/AuthContext';
import UserService from '../../../services/user.service';
import { VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES } from '../../../utils/appConstant';
import { AuthRoutes, Modules } from '../../../enums';
import Header from './components/Header';

const ChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext)

  const [formData, setFormData] = useState<ChangePasswordInterface>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState<{
    currentPassword: boolean
    newPassword: boolean
    confirmPassword: boolean
  }>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  })

  const [validation, setValidation] = useState<Validation>({ validate: true, fields: [] });
  const [loading, setLoading] = useState(false);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFormData((prev) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setFormData]);

  const handleInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateChangePasswordForm({
      ...formData,
      [name]: event.target.value.trim(),
    }, name);
    setValidation(newValidation);
  }, [formData.currentPassword, formData.newPassword, formData.confirmPassword]);

  const handleSubmit = useCallback(async (event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateChangePasswordForm(formData);
    setValidation(newValidation);

    if (!newValidation.validate) return;

    setLoading(true);

    try {
      const data: any = await UserService.changePassword({
        current_password: formData.currentPassword.trim(),
        new_password: formData.newPassword.trim(),
        confirm_password: formData.confirmPassword.trim(),
      });

      if (data.success !== false) {
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.changePassword} />, { icon: false });
        await logout();
        navigate(Modules.auth);
      }

      setLoading(false);
    } catch (err: any) {
      setLoading(false);
    }
  }, [formData.currentPassword, formData.newPassword, formData.confirmPassword]);

  const handleKeyDown = useCallback((e: any) => {
    if (e.key === 'Enter') handleSubmit();
  }, [handleSubmit]);

  const handleShowPasswordChange = useCallback((name: keyof ChangePasswordInterface) => {
    setShowPassword((prev) => {
      return {
        ...prev,
        [name]: !showPassword[name],
      };
    });
  }, [showPassword.confirmPassword, showPassword.currentPassword, showPassword.newPassword])

  const handleCancel = () => {
    navigate(Modules.home + AuthRoutes.dashboard);
  };

  return (
    <>
      <Header />

      <div className='card mb-6'>
        <h5 className='card-header'>
          {t('changePassword.pageTexts.header.title')}
        </h5>

        <hr className='m-0'></hr>

        <form className='card-body' autoComplete='off'>
          <div className='row g-6 mb-6'>
            <div className='col-6'>
              <div className="input-group input-group-merge">
                <div className='form-floating form-floating-outline'>
                  <Input
                    type={showPassword.currentPassword ? 'text' : 'password'}
                    id={VALIDATION_FIELD_MAPPING.currentPassword}
                    name={VALIDATION_FIELD_MAPPING.currentPassword}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.currentPassword)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.currentPassword)?.message ?? ''}
                    label={t('changePassword.pageTexts.form.currentPassword.label')}
                    placeholder={t('changePassword.pageTexts.form.currentPassword.placeholder')}
                    value={formData.currentPassword}
                    onKeyDown={handleKeyDown}
                    onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.currentPassword)}
                    onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.currentPassword)}
                  />
                </div>
                <span className="password-eye-icon input-group-text cursor-pointer" onClick={() => handleShowPasswordChange(VALIDATION_FIELD_MAPPING.currentPassword as keyof ChangePasswordInterface)}>
                  <i className={`ri-${showPassword.currentPassword ? 'eye' : 'eye-off'}-line`}></i>
                </span>
              </div>
            </div>
          </div>
          <div className='row g-6'>
            <div className='col-6'>
              <div className="input-group input-group-merge">
                <div className='form-floating form-floating-outline'>
                  <Input
                    type={showPassword.newPassword ? 'text' : 'password'}
                    id={VALIDATION_FIELD_MAPPING.newPassword}
                    name={VALIDATION_FIELD_MAPPING.newPassword}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.newPassword)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.newPassword)?.message ?? ''}
                    label={t('changePassword.pageTexts.form.newPassword.label')}
                    placeholder={t('changePassword.pageTexts.form.newPassword.placeholder')}
                    value={formData.newPassword}
                    onKeyDown={handleKeyDown}
                    onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.newPassword)}
                    onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.newPassword)}
                  />
                </div>
                <span className="password-eye-icon input-group-text cursor-pointer" onClick={() => handleShowPasswordChange(VALIDATION_FIELD_MAPPING.newPassword as keyof ChangePasswordInterface)}>
                  <i className={`ri-${showPassword.newPassword ? 'eye' : 'eye-off'}-line`}></i>
                </span>
              </div>
            </div>

            <div className='col-6'>
              <div className="input-group input-group-merge">
                <div className='form-floating form-floating-outline'>
                  <Input
                    type={showPassword.confirmPassword ? 'text' : 'password'}
                    id={VALIDATION_FIELD_MAPPING.confirmPassword}
                    name={VALIDATION_FIELD_MAPPING.confirmPassword}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.confirmPassword)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.confirmPassword)?.message ?? ''}
                    label={t('changePassword.pageTexts.form.confirmPassword.label')}
                    placeholder={t('changePassword.pageTexts.form.confirmPassword.placeholder')}
                    value={formData.confirmPassword}
                    onKeyDown={handleKeyDown}
                    onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.confirmPassword)}
                    onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.confirmPassword)}
                  />
                </div>
                <span className="password-eye-icon input-group-text cursor-pointer" onClick={() => handleShowPasswordChange(VALIDATION_FIELD_MAPPING.confirmPassword as keyof ChangePasswordInterface)}>
                  <i className={`ri-${showPassword.confirmPassword ? 'eye' : 'eye-off'}-line`}></i>
                </span>
              </div>
            </div>
          </div>
        </form>

        <hr className='m-0'></hr>

        <div className='p-4'>
          <Button
            className='btn btn-primary me-4'
            text={t('changePassword.pageTexts.form.buttons.save')}
            disabled={loading}
            loading={loading}
            onClick={(e) => handleSubmit(e)}
          />
          <Button
            className='btn btn-outline-secondary'
            text={t('changePassword.pageTexts.form.buttons.cancel')}
            onClick={handleCancel}
          />
        </div>
      </div>
    </>
  );
};

export default ChangePassword;